import React from 'react'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import AddOnRenewAction from './AddOnRenewAction'
import StatusBadge from '../shared/StatusBadge'
import { addOnRenewalState } from '../shared/addOnRenewal'
import moment from 'moment'

/**
 * The primary member's add-on members.
 *
 * Rows come from the addOns collection via GET /organization/:orgId/memberFamily,
 * which is the primary/add-on association itself - not the invite log. That
 * matters for what can be shown: an accepted row is linked to a real member
 * record, so it has a name, and its status is the member's actual state rather
 * than "we emailed this address once".
 *
 * Callers pass only active rows; revoked and removed ones are history. The
 * one exception is a removed row whose access has not run out (spec 7a),
 * which the primary sees as "Removed, access until {date}".
 *
 * Renewal (flow A, spec section 3) is shown to the primary only:
 *   isPrimary        the viewer is the family's primary
 *   renewWindowOpen  the primary's own renewal window (isInRenewalWindow)
 *   primaryRenewed   the primary has renewed; until then Renew is disabled
 *   paysForAddOns    the primary's family-wide billing answer
 *   primaryRenewalStart  'YYYY-MM-DD' start of the primary's current renewal;
 *                    an add-on renewal for an earlier term counts as none
 *   onRenewalSent    called after a renewal email goes out
 *   primaryName      the primary's name, for "{primary} pays"
 *
 * Once a renewal email has gone, the row says when and to whom, and who pays.
 */

const PENDING = ['emailed', 'in_progress', 'signed']

const renewalDetails = (member, primaryName) => {
    const renewal = member.renewal
    if (!renewal || PENDING.indexOf(renewal.status) === -1) return null
    const sent = renewal.lastEmailedAt ? moment(renewal.lastEmailedAt).format('MMM D') : null
    const paysPrimary = renewal.status === 'signed' ? renewal.payer === 'primary' : renewal.primaryPays === true
    return {
        sentLine: sent ? `Email sent ${sent}${member.email ? ` to ${member.email}` : ''}` : null,
        payerLine: paysPrimary ? `${primaryName || 'You'} pay${primaryName ? 's' : ''}` : 'Pays for themselves'
    }
}
const AddOnMemberList = ({
    members = [],
    onResend,
    isPrimary = false,
    renewWindowOpen = false,
    primaryRenewed = false,
    paysForAddOns = false,
    primaryRenewalStart = null,
    onRenewalSent,
    primaryName = null
}) => {

    if (members.length === 0) {
        return 'None'
    }

    return members.map((member) => {
        const renewal = isPrimary ? addOnRenewalState(member, { primaryRenewalStart }) : { action: null, statusLine: null }
        const showRenew = renewWindowOpen && !!renewal.action
        const details = isPrimary ? renewalDetails(member, primaryName) : null

        return <Grid container item key={member.addOnId} justifyContent='space-between' alignItems='center'>
            <Grid item>
                {member.name || member.email}
                {member.name && (
                    <Typography variant='body2' color='textSecondary'>{member.email}</Typography>
                )}
            </Grid>
            <Grid item>
                {member.membershipType} {member.status === 'invited' && ' (pending)'}
            </Grid>
            <Grid item>
                {/* Only a pending invite can be resent. An accepted member has
                    already joined, which the invite log could never tell us. */}
                {member.status === 'invited'
                    ? <Button color='primary' size='small' onClick={() => onResend(member)}>Resend Invite</Button>
                    : ''}
                {showRenew && (
                    <AddOnRenewAction
                        member={member}
                        action={renewal.action}
                        primaryRenewed={primaryRenewed}
                        paysForAddOns={paysForAddOns}
                        primaryName={primaryName}
                        onSent={onRenewalSent}
                    />
                )}
                {renewal.statusLine && (
                    <div style={{ marginTop: 6, textAlign: 'right' }}>
                        <StatusBadge label={renewal.statusLine} />
                        {details && <Typography variant='body2' color='textSecondary' data-testid='renewal-details' style={{ marginTop: 4 }}>
                            {[details.sentLine, details.payerLine].filter(Boolean).join(' · ')}
                        </Typography>}
                    </div>
                )}
            </Grid>
        </Grid>
    })
}

export default AddOnMemberList
